import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FeatherIcon } from '../../components/FeatherIcon'
import { stageMediaObject } from '../../chat/upload'
import { Sentinel } from '../../library/Sentinel'
import { useMediaSrc } from '../../library/useMediaSrc'
import type { VaultItem } from '../../library/vault/vaultData'
import { Avatar } from '../Avatar'
import { OF, type OfApi } from '../ofApi'
import type { OfUser } from '../posts'
import { storyCards, storyMedia, toStoryRefs, type StoryCard, type StoryRef } from './homeData'
import { StoryEditor } from './StoryEditor'

const BATCH = 10
const ACCEPT = 'image/*,video/*'

/**
 * The stories row: always-visible "Add" when writable, "Your story", then
 * subscribed creators. Pick file → OnlyFans-style editor → converter object
 * → POST stories (no vault Save-for-later).
 */
export function Stories({
  of,
  me,
  accountId,
  canWrite,
  onOpen,
  say,
}: {
  of: OfApi
  me: OfUser
  accountId: string
  canWrite: boolean
  onOpen: (items: VaultItem[]) => void
  say: (text: string) => void
}) {
  const { t } = useTranslation('ofHome')
  const [own, setOwn] = useState<VaultItem[]>([])
  const [cards, setCards] = useState<StoryCard[]>([])
  const refs = useRef<StoryRef[]>([])
  const next = useRef(0)
  const [hasMore, setHasMore] = useState(false)
  const busy = useRef(false)
  const fileRef = useRef<HTMLInputElement>(null)
  const [draftFile, setDraftFile] = useState<File | null>(null)
  const [publishing, setPublishing] = useState(false)

  const reloadOwn = useCallback(async () => {
    try {
      setOwn(storyMedia(await of.get('users/me/stories', {}, OF)))
    } catch {
      /* keep previous */
    }
  }, [of])

  const loadMore = useCallback(async () => {
    if (busy.current) return
    const batch = refs.current.slice(next.current, next.current + BATCH)
    if (!batch.length) return setHasMore(false)
    busy.current = true
    try {
      const query: Record<string, number> = {}
      for (const r of [...batch].sort((a, b) => a.userId - b.userId)) query[`map[${r.userId}]`] = r.storyId
      const items = await of.get<Record<string, Record<string, unknown>>>('stories/items', query, OF)
      next.current += batch.length
      setCards((all) => [...all, ...storyCards(batch, items ?? {})])
      setHasMore(next.current < refs.current.length)
    } catch {
      setHasMore(false)
    } finally {
      busy.current = false
    }
  }, [of])

  useEffect(() => {
    let live = true
    void reloadOwn()
    of.get('stories/map', {}, OF)
      .then((p) => {
        if (!live) return
        refs.current = toStoryRefs(p)
        next.current = 0
        setCards([])
        void loadMore()
      })
      .catch(() => undefined)
    return () => {
      live = false
    }
  }, [of, loadMore, reloadOwn])

  const openCreator = async (c: StoryCard) => {
    try {
      const items = storyMedia(await of.get(`users/${c.userId}/stories`, {}, `${OF}/${c.user.username}`))
      if (items.length) onOpen(items)
      else say(t('stories.none'))
    } catch (e) {
      say(e instanceof Error && e.message ? e.message : t('error'))
    }
  }

  const onPick = (list: FileList | null) => {
    const file = list?.[0]
    if (fileRef.current) fileRef.current.value = ''
    if (!file) return
    if (!file.type.startsWith('image/') && !file.type.startsWith('video/')) {
      say(t('stories.photoOrVideo'))
      return
    }
    setDraftFile(file)
  }

  const publish = async (file: File) => {
    setPublishing(true)
    try {
      const media = await stageMediaObject(accountId, '', file)
      await of.send('POST', 'stories', `${OF}/`, {
        mediaFiles: [media],
        rfTag: [],
        rfGuest: [],
        rfPartner: [],
      })
      setDraftFile(null)
      await reloadOwn()
      say(t('stories.added'))
    } finally {
      setPublishing(false)
    }
  }

  if (!canWrite && !own.length && !cards.length) return null

  return (
    <>
      <div className="ofh-stories" role="list" aria-label={t('stories.label')}>
        {canWrite && (
          <>
            <input
              ref={fileRef}
              type="file"
              accept={ACCEPT}
              hidden
              onChange={(e) => onPick(e.target.files)}
            />
            <button
              type="button"
              role="listitem"
              className="ofh-story ofh-story--add"
              aria-label={t('stories.add')}
              disabled={publishing}
              onClick={() => fileRef.current?.click()}
            >
              <span className="ofh-story-ring ofh-story-ring--add">
                <Avatar user={me} size={36} />
                <span className="ofh-story-plus" aria-hidden="true">
                  <FeatherIcon name="plus" size={14} />
                </span>
              </span>
              <span className="ofh-story-name">{t('stories.add')}</span>
            </button>
          </>
        )}
        {own.length > 0 && <StoryTile label={t('stories.yours')} preview={own[0]?.thumb ?? ''} user={me} onOpen={() => onOpen(own)} />}
        {cards.map((c) => (
          <StoryTile key={c.userId} label={c.user.name} preview={c.preview} user={c.user} watched={c.watched} onOpen={() => void openCreator(c)} />
        ))}
        {hasMore && <Sentinel onVisible={loadMore} token={cards.length} />}
      </div>
      {draftFile && (
        <StoryEditor file={draftFile} onClose={() => setDraftFile(null)} onSave={publish} />
      )}
    </>
  )
}

function StoryTile({ label, preview, user, watched = false, onOpen }: { label: string; preview: string; user: OfUser; watched?: boolean; onOpen: () => void }) {
  const { ref, src } = useMediaSrc(preview)
  return (
    <button
      type="button"
      role="listitem"
      ref={ref as React.RefObject<HTMLButtonElement>}
      className={`ofh-story ${watched ? 'is-watched' : ''}`}
      style={src ? { backgroundImage: `url("${src}")` } : undefined}
      aria-label={label}
      onClick={onOpen}
    >
      <span className="ofh-story-ring">
        <Avatar user={user} size={36} />
      </span>
      <span className="ofh-story-name">{label}</span>
    </button>
  )
}
